import { h, Component, Host, State, Prop, Watch } from '@stencil/core';

import type { ChartDataInterface } from '../../../services/interfaces';

@Component({
  tag: 'pk-column-charts',
  styleUrl: './scss/pk-column-charts.scss',
  shadow: true,
})
export class PkColumnCharts {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() chartData: Array<ChartDataInterface> = [];

  // Public Property API
  // ========================

  /** Object with values needed in order to display the chart.
   * Example of data: [
        { "value": 75, "label": "1-15" },
        { "value": 155, "label": "ET 1st half" },
        { "value": 20, "label": "45+" }
       */
  @Prop() data: string | Array<ChartDataInterface>;

  // Prop lifecycle events
  // =======================

  @Watch('data')
  setChartData() {
    this.chartData = Array.isArray(this.data) ? this.data : JSON.parse(this.data);
    this.setChartHeight();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called when the Avatar is clicked */

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.setChartData();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private setChartHeight = () => {
    let max = this.chartData[0].value;

    for (const elem of this.chartData) {
      if (max < elem.value) max = elem.value;
    }

    for (const elem of this.chartData) {
      elem['height'] = (elem.value * 100) / max;
    }
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host
        style={{
          '--column-charts-columns-number': `${this.chartData?.length ?? 0}`,
        }}
      >
        <div class="chart-container grid h-auto" part="container">
          {this.chartData.map((el) => (
            <div
              class="flex min-w-[15px] flex-col items-center justify-end"
              key={`${el.label}-${el.value}`}
              part="item"
            >
              <span class="mb-xs2 text-xs leading-[15px] text-[color:--column-charts-number-item--color]" part="value">
                {el.value}
              </span>
              <div
                class="chart-item-bar mb-xs w-[5px] origin-[50%_100%] animate-[animation-bar_1s] rounded-full bg-[color:--column-charts-bar-item--color]"
                style={{ height: `${el.height}px` }}
                part="item-bar"
              ></div>
            </div>
          ))}
          {this.chartData.map((el) => (
            <span
              class="flex min-w-[15px] items-center text-xs text-[color:--column-charts-legend-item--color] [writing-mode:vertical-rl]"
              key={`${el.value}-${el.label}`}
              part="legend"
            >
              {el.label}
            </span>
          ))}
        </div>
      </Host>
    );
  }
}
